import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { uploadsAPI } from '../services/api';

const ImagePreviewModal = ({ id, url, filename, thumbnailUrl, onClose }) => {
  const [blobUrl, setBlobUrl] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [contentType, setContentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState('');
  
  const displayUrl = blobUrl || url || '';
  
  const isImage = useMemo(() => {
    const nameOrUrl = (filename || displayUrl || '').toLowerCase();
    if (contentType && contentType.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(nameOrUrl);
  }, [filename, displayUrl, contentType]);
  
  const isPdf = useMemo(() => {
    const nameOrUrl = (filename || displayUrl || '').toLowerCase();
    if (contentType === 'application/pdf') return true;
    return nameOrUrl.endsWith('.pdf');
  }, [filename, displayUrl, contentType]);
  
  // Check if file can be natively previewed in browser (images, PDFs)
  const canPreviewNatively = useMemo(() => {
    return isImage || isPdf;
  }, [isImage, isPdf]);

  // Download original file with original filename
  const handleDownload = useCallback(() => {
    if (!displayUrl) return;
    const link = document.createElement('a');
    link.href = displayUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [displayUrl, filename]);

  // Fetch original file blob
  useEffect(() => {
    let currentUrl = '';
    let cancelled = false;
    async function run() {
      if (!id) { setBlobUrl(''); return; }
      try {
        setLoading(true);
        const res = await uploadsAPI.fetchBlob(id);
        const mime = res?.headers?.['content-type'] || '';
        setContentType(mime);
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: mime || 'application/octet-stream' });
        currentUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(currentUrl);
      } catch {
        if (!cancelled) setBlobUrl('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [id]);

  // Fetch PDF preview for non-natively-previewable files
  useEffect(() => {
    let pdfUrl = '';
    let cancelled = false;
    
    async function fetchPdfPreview() {
      // Only fetch PDF preview if we have an id and file can't be previewed natively
      if (!id || canPreviewNatively || loading) return;
      
      try {
        setPdfLoading(true);
        setPdfError('');
        const res = await uploadsAPI.fetchPdfPreview(id);
        if (res?.data) {
          const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'application/pdf' });
          pdfUrl = URL.createObjectURL(blob);
          if (!cancelled) setPdfPreviewUrl(pdfUrl);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err?.response?.data?.error || 'Preview not available';
          setPdfError(errorMsg);
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }
    
    fetchPdfPreview();
    
    return () => {
      cancelled = true;
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [id, canPreviewNatively, loading]);

  if (!displayUrl && !loading) return null;

  // Determine what URL to use for preview display
  const previewDisplayUrl = canPreviewNatively ? displayUrl : pdfPreviewUrl;
  const showPdfPreview = !canPreviewNatively && pdfPreviewUrl;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[80]" onClick={onClose}>
      <div className="max-w-5xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-2">
          <div className="text-white text-sm truncate">{filename || url}</div>
          <div className="flex gap-2">
            {(previewDisplayUrl || showPdfPreview) && (
              <a href={previewDisplayUrl} target="_blank" rel="noreferrer" className="btn btn-secondary btn-sm">
                Open in new tab
              </a>
            )}
            {displayUrl && (
              <button className="btn btn-secondary btn-sm" onClick={handleDownload}>
                Download{!canPreviewNatively ? ' Original' : ''}
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
        <div className="bg-white rounded-lg p-3 shadow-lg max-h-[85vh] overflow-auto">
          {loading ? (
            <div className="text-center text-gray-600 py-12">Loading…</div>
          ) : isImage ? (
            <img src={displayUrl} alt={filename || 'Preview'} className="max-h-[80vh] w-auto mx-auto object-contain" />
          ) : isPdf ? (
            <iframe src={displayUrl} title={filename || 'Preview'} className="w-full h-[80vh]" />
          ) : pdfLoading ? (
            <div className="text-center text-gray-600 py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p>Converting to PDF for preview…</p>
            </div>
          ) : showPdfPreview ? (
            <iframe src={pdfPreviewUrl} title={filename || 'Preview'} className="w-full h-[80vh]" />
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-600 mb-4">
                {thumbnailUrl ? (
                  <img 
                    src={thumbnailUrl} 
                    alt={filename || 'Preview'} 
                    className="max-w-md max-h-64 mx-auto mb-4 object-contain rounded shadow-lg"
                  />
                ) : (
                  <svg className="w-16 h-16 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                <p className="text-lg font-medium">{filename || 'File'}</p>
                <p className="text-sm text-gray-500 mt-1">
                  {pdfError || 'Preview not available'}
                </p>
              </div>
              <button className="btn btn-primary" onClick={handleDownload}>
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
